import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { bucketlistService } from "api/services/bucketlist.service";
import ContentHeader from "components/Layout/ContentHeader";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import { getApiErrorMessage } from "utils/api-error";
import { BUCKETLIST_INTERVAL_OPTIONS, colorForCategory, labelForCategory } from "utils/constant/bucketlist";

import type { BucketlistCategory, BucketlistCategoryStat, BucketlistStats as BucketlistStatsData, BucketlistTrend, BucketlistTrendInterval } from "api/types/bucketlist.types";

import "pages/AdminPage.scss";
import "./BucketlistStats.scss";

const pct = (v: number): string => `${(v * 100).toFixed(1)}%`;

// recharts 차트 요소 클릭 시 전달되는 객체 (데이터가 sector/bar 에 spread 되어 category 가 최상위 또는 payload 안에 존재)
type CategoryClickEntry = { category?: string; payload?: { category?: string } };

const BucketlistStats: React.FC = () => {
  const navigate = useNavigate();

  // 기간 필터 (통계 + 추이 공통)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");

  // 통계
  const [stats, setStats] = useState<BucketlistStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");

  // 추이
  const [interval, setInterval] = useState<BucketlistTrendInterval>("month");
  const [trendCategory, setTrendCategory] = useState("");
  const [trend, setTrend] = useState<BucketlistTrend | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState("");

  // ── 통계 조회 ──
  useEffect(() => {
    setStatsLoading(true);
    setStatsError("");
    bucketlistService
      .stats({
        ...(appliedStart ? { start_date: appliedStart } : {}),
        ...(appliedEnd ? { end_date: appliedEnd } : {}),
      })
      .then((res) => setStats(res.data))
      .catch((err) => {
        setStatsError(getApiErrorMessage(err));
        setStats(null);
      })
      .finally(() => setStatsLoading(false));
  }, [appliedStart, appliedEnd]);

  // ── 추이 조회 ──
  useEffect(() => {
    setTrendLoading(true);
    setTrendError("");
    bucketlistService
      .trend({
        interval,
        ...(trendCategory ? { category: trendCategory as BucketlistCategory } : {}),
        ...(appliedStart ? { start_date: appliedStart } : {}),
        ...(appliedEnd ? { end_date: appliedEnd } : {}),
      })
      .then((res) => setTrend(res.data))
      .catch((err) => {
        setTrendError(getApiErrorMessage(err));
        setTrend(null);
      })
      .finally(() => setTrendLoading(false));
  }, [interval, trendCategory, appliedStart, appliedEnd]);

  const applyPeriod = () => {
    if (startDate && endDate && startDate > endDate) {
      setStatsError("시작일은 종료일보다 클 수 없습니다.");
      return;
    }
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const resetPeriod = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStart("");
    setAppliedEnd("");
  };

  // count 내림차순 정렬 (백엔드가 이미 정렬하지만 방어적으로)
  const ranked: BucketlistCategoryStat[] = useMemo(() => {
    if (!stats) return [];
    return [...stats.by_category].sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
  }, [stats]);

  const pieData = useMemo(() => ranked.filter((c) => c.count > 0), [ranked]);
  const activeCategoryCount = pieData.length;

  const topCategory = ranked[0] && ranked[0].count > 0 ? ranked[0] : null;
  const bestAchievement = useMemo(() => {
    const withData = ranked.filter((c) => c.count > 0);
    if (withData.length === 0) return null;
    return [...withData].sort((a, b) => b.achievement_rate - a.achievement_rate)[0];
  }, [ranked]);

  const categoryOptions = useMemo(() => [{ value: "", label: "전체 카테고리" }, ...ranked.map((c) => ({ value: c.category, label: c.label || labelForCategory(c.category) }))], [ranked]);

  // 카테고리 클릭 시 상세 목록 페이지로 이동 (기간 필터를 쿼리로 전달)
  const openCategory = (category?: string) => {
    if (!category) return;
    const query = new URLSearchParams();
    if (appliedStart) query.set("start", appliedStart);
    if (appliedEnd) query.set("end", appliedEnd);
    const qs = query.toString();
    navigate(`/bucketlist-stats/${category}${qs ? `?${qs}` : ""}`);
  };

  const hasData = !!stats && stats.total > 0;

  return (
    <div className="admin-page bucketlist-stats">
      <ContentHeader title="버킷리스트 통계" description="사용자가 등록한 버킷리스트를 카테고리 단위로 분석합니다. 고객 니즈 파악·사업 확장 자료로 활용하세요." />

      {/* ── 기간 필터 ── */}
      <div className="admin-page__card bucketlist-stats__filter-card">
        <div className="admin-page__filter">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: 160 }} aria-label="시작일" />
          <span className="bucketlist-stats__filter-sep">~</span>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: 160 }} aria-label="종료일" />
          <Button variant="secondary" size="sm" onClick={applyPeriod}>
            조회
          </Button>
          <Button variant="ghost" size="sm" onClick={resetPeriod}>
            전체 기간
          </Button>
        </div>
      </div>

      {statsError && <div className="bucketlist-stats__error">{statsError}</div>}
      {statsLoading && <div className="bucketlist-stats__loading">통계를 불러오는 중...</div>}

      {!statsLoading && stats && (
        <>
          {/* ── KPI ── */}
          <section className="stat-cards bucketlist-stats__kpi">
            <div className="stat-card stat-card--primary">
              <div className="stat-card__label">총 등록 수</div>
              <div className="stat-card__value">{stats.total.toLocaleString()}</div>
              <div className="stat-card__sub">개 버킷리스트</div>
            </div>
            <div className="stat-card stat-card--success">
              <div className="stat-card__label">달성 완료</div>
              <div className="stat-card__value">{stats.achieved_total.toLocaleString()}</div>
              <div className="stat-card__sub">개 달성</div>
            </div>
            <div className="stat-card stat-card--info">
              <div className="stat-card__label">전체 달성률</div>
              <div className="stat-card__value">{pct(stats.achievement_rate)}</div>
              <div className="stat-card__sub">달성 / 전체</div>
            </div>
            <div className="stat-card stat-card--warning">
              <div className="stat-card__label">활성 카테고리</div>
              <div className="stat-card__value">{activeCategoryCount}</div>
              <div className="stat-card__sub">/ {ranked.length} 카테고리</div>
            </div>
          </section>

          {!hasData ? (
            <div className="admin-page__card bucketlist-stats__empty">선택한 기간에 등록된 버킷리스트가 없습니다.</div>
          ) : (
            <>
              {/* ── 마케팅 인사이트 ── */}
              <section className="bucketlist-stats__insights">
                <div className="insight-card">
                  <span className="insight-card__tag">고객 니즈 TOP</span>
                  <span className="insight-card__title">{topCategory ? topCategory.label : "-"}</span>
                  <span className="insight-card__desc">
                    가장 많이 등록된 카테고리 · 전체의 <b>{topCategory ? pct(topCategory.share) : "-"}</b>
                    {topCategory ? ` (${topCategory.count.toLocaleString()}건)` : ""}
                  </span>
                </div>
                <div className="insight-card">
                  <span className="insight-card__tag insight-card__tag--green">실현 니즈</span>
                  <span className="insight-card__title">{bestAchievement ? bestAchievement.label : "-"}</span>
                  <span className="insight-card__desc">
                    달성률이 가장 높은 카테고리 · <b>{bestAchievement ? pct(bestAchievement.achievement_rate) : "-"}</b> 달성
                  </span>
                </div>
                <div className="insight-card">
                  <span className="insight-card__tag insight-card__tag--purple">잠재 니즈</span>
                  <span className="insight-card__title">{topCategory && bestAchievement ? `${topCategory.label} × ${bestAchievement.label}` : "-"}</span>
                  <span className="insight-card__desc">수요는 높지만 미달성이 많은 영역은 상품·프로그램 확장 기회입니다.</span>
                </div>
              </section>

              {/* ── 도넛 + 랭킹 ── */}
              <section className="bucketlist-stats__row">
                <div className="admin-page__card bucketlist-stats__panel">
                  <div className="bucketlist-stats__panel-title">카테고리 비중</div>
                  <p className="bucketlist-stats__hint">차트 조각을 클릭하면 상세 목록이 열립니다.</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={pieData} dataKey="count" nameKey="label" innerRadius={72} outerRadius={112} paddingAngle={2} onClick={(entry: CategoryClickEntry) => openCategory(entry?.category ?? entry?.payload?.category)}>
                        {pieData.map((entry) => (
                          <Cell key={entry.category} fill={colorForCategory(entry.category)} cursor="pointer" />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: unknown, _name: unknown, item: { payload?: BucketlistCategoryStat }) => [`${Number(value).toLocaleString()}건 (${pct(item?.payload?.share ?? 0)})`, item?.payload?.label]} />
                      <Legend formatter={(_value, entry: { payload?: { label?: string } }) => entry?.payload?.label} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="admin-page__card bucketlist-stats__panel">
                  <div className="bucketlist-stats__panel-title">카테고리 순위</div>
                  <p className="bucketlist-stats__hint">행을 클릭하면 상세 목록이 열립니다.</p>
                  <table className="bucketlist-stats__rank-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>카테고리</th>
                        <th>등록</th>
                        <th>비중</th>
                        <th>달성률</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.map((c, idx) => (
                        <tr key={c.category} className="bucketlist-stats__rank-row" onClick={() => openCategory(c.category)}>
                          <td className="bucketlist-stats__rank-num">{idx + 1}</td>
                          <td>
                            <span className="bucketlist-stats__dot" style={{ background: colorForCategory(c.category) }} />
                            {c.label}
                          </td>
                          <td>{c.count.toLocaleString()}</td>
                          <td>{pct(c.share)}</td>
                          <td>
                            <div className="bucketlist-stats__rate">
                              <div className="bucketlist-stats__rate-bar">
                                <div className="bucketlist-stats__rate-fill" style={{ width: `${Math.round(c.achievement_rate * 100)}%` }} />
                              </div>
                              <span>{pct(c.achievement_rate)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ── 카테고리별 달성 현황 ── */}
              <section className="admin-page__card bucketlist-stats__panel">
                <div className="bucketlist-stats__panel-title">카테고리별 달성 현황</div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ranked} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barCategoryGap="24%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: unknown, name: unknown) => [`${Number(value).toLocaleString()}건`, name === "achieved" ? "달성" : "미달성"]} />
                    <Legend formatter={(value) => (value === "achieved" ? "달성" : "미달성")} iconType="circle" />
                    <Bar dataKey="achieved" stackId="a" fill="#10b981" cursor="pointer" onClick={(entry: CategoryClickEntry) => openCategory(entry?.category ?? entry?.payload?.category)} />
                    <Bar dataKey="unachieved" stackId="a" fill="#e4e4e7" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(entry: CategoryClickEntry) => openCategory(entry?.category ?? entry?.payload?.category)} />
                  </BarChart>
                </ResponsiveContainer>
              </section>
            </>
          )}
        </>
      )}

      {/* ── 등록 추이 ── */}
      <section className="admin-page__card bucketlist-stats__panel">
        <div className="bucketlist-stats__panel-header">
          <div className="bucketlist-stats__panel-title">신규 등록 추이</div>
          <div className="bucketlist-stats__panel-controls">
            <Select value={trendCategory} onChange={(e) => setTrendCategory(e.target.value)} options={categoryOptions} style={{ width: 150 }} />
            <Select value={interval} onChange={(e) => setInterval(e.target.value as BucketlistTrendInterval)} options={BUCKETLIST_INTERVAL_OPTIONS} style={{ width: 110 }} />
          </div>
        </div>

        {trendError && <div className="bucketlist-stats__error">{trendError}</div>}
        {trendLoading ? (
          <div className="bucketlist-stats__loading">추이를 불러오는 중...</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend?.series ?? []} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: unknown) => [`${Number(value).toLocaleString()}건`, "신규 등록"]} />
              <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>
    </div>
  );
};

export default BucketlistStats;
